import { useMemo, useState } from "react";
import {
  SaleDepositResponse,
  UpdateSaleDepositExpensesRequest,
} from "../../types/sale-deposit.type";
import {
  FIXED_EXPENSE_FIELDS,
  FixedExpenseField,
  parseNonNegativeMoney,
  toDraftAmount,
} from "./transfer.utils";

/**
 * ยอดฝากเก็บ rides along with the five expense buckets because it is filed on the same form
 * and moves ที่ต้องโอน the same way — it is just added rather than subtracted.
 */
export type ExpenseMoneyField = FixedExpenseField | "deposit_collection_amount";

export type ExpenseMoneyDraft = Record<ExpenseMoneyField, string>;

/**
 * One free-form รายการเพิ่มเติม while it is being edited. `id` is a client-side key only:
 * the array is replaced wholesale on save, so a stored uuid would be meaningless here.
 */
export interface AdditionalExpenseDraft {
  id: string;
  description: string;
  amount: string;
}

const EMPTY_MONEY: ExpenseMoneyDraft = {
  fuel_expense_amount: "",
  transfer_fee_amount: "",
  toll_fee_amount: "",
  parking_fee_amount: "",
  other_expense_amount: "",
  deposit_collection_amount: "",
};

const AMOUNT_ERROR = "จำนวนเงินต้องเป็น 0 หรือมากกว่า";
const DESCRIPTION_ERROR = "กรุณาระบุรายการ";

let additionalExpenseSequence = 0;
const nextAdditionalExpenseId = () =>
  `expense-draft-${additionalExpenseSequence++}`;

/** The error key a free-form row's description / amount reports under. */
export const additionalExpenseErrorKey = (
  id: string,
  field: "description" | "amount",
) => `${id}.${field}`;

/**
 * The editable half of รายละเอียดค่าใช้จ่าย: what the user has typed, what is wrong with it,
 * and the request that comes out the other end.
 *
 * Amounts are held as strings rather than numbers because a half-typed "12." is a legitimate
 * intermediate state, and a blank field means 0 — the same convention แจ้งยอดโอน uses, so a
 * figure filed there and corrected here reads identically on both screens.
 */
export function useExpenseDraft() {
  const [money, setMoney] = useState<ExpenseMoneyDraft>(EMPTY_MONEY);
  const [additionalExpenses, setAdditionalExpenses] = useState<
    AdditionalExpenseDraft[]
  >([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  /** Seed the draft from a stored row — called when edit mode opens, never while typing. */
  const reset = (row: SaleDepositResponse) => {
    setMoney({
      fuel_expense_amount: toDraftAmount(row.fuel_expense_amount),
      transfer_fee_amount: toDraftAmount(row.transfer_fee_amount),
      toll_fee_amount: toDraftAmount(row.toll_fee_amount),
      parking_fee_amount: toDraftAmount(row.parking_fee_amount),
      other_expense_amount: toDraftAmount(row.other_expense_amount),
      deposit_collection_amount: toDraftAmount(row.deposit_collection_amount),
    });
    setAdditionalExpenses(
      (row.additional_expenses ?? []).map((expense) => ({
        id: nextAdditionalExpenseId(),
        description: expense.description,
        amount: toDraftAmount(expense.amount),
      })),
    );
    setErrors({});
  };

  const clearError = (key: string) =>
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const updateMoney = (field: ExpenseMoneyField, value: string) => {
    setMoney((prev) => ({ ...prev, [field]: value }));
    clearError(field);
  };

  const addExpense = () =>
    setAdditionalExpenses((prev) => [
      ...prev,
      { id: nextAdditionalExpenseId(), description: "", amount: "" },
    ]);

  const updateExpense = (
    id: string,
    patch: Partial<Pick<AdditionalExpenseDraft, "description" | "amount">>,
  ) => {
    setAdditionalExpenses((prev) =>
      prev.map((expense) =>
        expense.id === id ? { ...expense, ...patch } : expense,
      ),
    );
    Object.keys(patch).forEach((field) =>
      clearError(additionalExpenseErrorKey(id, field as "description" | "amount")),
    );
  };

  const removeExpense = (id: string) => {
    setAdditionalExpenses((prev) => prev.filter((expense) => expense.id !== id));
    setErrors((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([key]) => !key.startsWith(`${id}.`)),
      ),
    );
  };

  /**
   * A live รวมค่าใช้จ่าย for the fields on screen — the five buckets plus the free-form rows,
   * excluding ยอดฝากเก็บ, which is not an expense. Null while any amount is unparseable, so
   * the preview says nothing rather than something wrong. This is a PREVIEW only: what the
   * report shows after saving is the server's own total.
   */
  const previewTotal = useMemo(() => {
    const amounts = [
      ...FIXED_EXPENSE_FIELDS.map(({ key }) => parseNonNegativeMoney(money[key])),
      ...additionalExpenses.map((expense) =>
        parseNonNegativeMoney(expense.amount),
      ),
    ];
    if (amounts.some((amount) => amount == null)) return null;
    return (amounts as number[]).reduce((sum, amount) => sum + amount, 0);
  }, [money, additionalExpenses]);

  /**
   * The request for PATCH /sale-deposits/:uuid/expenses, or null when the draft cannot be
   * saved — in which case `errors` now names every field at fault.
   *
   * Every key is sent, including the ones the user did not touch: the endpoint's PATCH
   * semantics would leave an omitted key alone, but the form owns all of them, so sending
   * the complete picture keeps what was saved identical to what was on screen.
   */
  const buildRequest = (): UpdateSaleDepositExpensesRequest | null => {
    const nextErrors: Record<string, string> = {};

    (Object.keys(money) as ExpenseMoneyField[]).forEach((field) => {
      if (parseNonNegativeMoney(money[field]) == null) {
        nextErrors[field] = AMOUNT_ERROR;
      }
    });

    additionalExpenses.forEach((expense) => {
      if (!expense.description.trim()) {
        nextErrors[additionalExpenseErrorKey(expense.id, "description")] =
          DESCRIPTION_ERROR;
      }
      if (parseNonNegativeMoney(expense.amount) == null) {
        nextErrors[additionalExpenseErrorKey(expense.id, "amount")] =
          AMOUNT_ERROR;
      }
    });

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return null;

    return {
      fuel_expense_amount: parseNonNegativeMoney(money.fuel_expense_amount) ?? 0,
      transfer_fee_amount: parseNonNegativeMoney(money.transfer_fee_amount) ?? 0,
      toll_fee_amount: parseNonNegativeMoney(money.toll_fee_amount) ?? 0,
      parking_fee_amount: parseNonNegativeMoney(money.parking_fee_amount) ?? 0,
      other_expense_amount:
        parseNonNegativeMoney(money.other_expense_amount) ?? 0,
      deposit_collection_amount:
        parseNonNegativeMoney(money.deposit_collection_amount) ?? 0,
      additional_expenses: additionalExpenses.map((expense) => ({
        description: expense.description.trim(),
        amount: parseNonNegativeMoney(expense.amount) ?? 0,
      })),
    };
  };

  return {
    money,
    additionalExpenses,
    errors,
    previewTotal,
    reset,
    updateMoney,
    addExpense,
    updateExpense,
    removeExpense,
    buildRequest,
  };
}
